import { useEffect, useState } from 'react';
import { Form } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { useQuery } from '@apollo/client';
// Api
import { LIST_CHANNEL_MONETIZATION_MILESTONES } from 'api/channelMonetization/queries';
// Types
import {
  ListChannelMonetizationMilestones,
  ListChannelMonetizationMilestonesVariables,
} from 'api/channelMonetization/types/ListChannelMonetizationMilestones';
import { ListChannelMonetizationPrograms_listChannelMonetizationPrograms } from 'api/channelMonetization/types/ListChannelMonetizationPrograms';
import { ChannelMonetizationMilestoneType } from 'api/graphql-global-types';
import { MilestoneRow } from './types';
// Helpers
import {
  currentMonthLabel,
  patchMilestoneRows,
  toMilestoneRow,
} from './helpers';
// Constants
import { DEFAULT_MILESTONES, DEFAULT_PROGRAM_FORM_VALUES } from './constants';

type UseProgramFormParams = {
  isVisible: boolean;
  isEdit: boolean;
  program: ListChannelMonetizationPrograms_listChannelMonetizationPrograms | null;
};

type UseProgramFormResult = {
  form: FormInstance;
  milestones: MilestoneRow[];
  loadingMilestones: boolean;
  updateMilestone: (
    type: ChannelMonetizationMilestoneType,
    patch: Partial<MilestoneRow>
  ) => void;
};

export const useProgramForm = ({
  isVisible,
  isEdit,
  program,
}: UseProgramFormParams): UseProgramFormResult => {
  const [form] = Form.useForm();
  const [milestones, setMilestones] =
    useState<MilestoneRow[]>(DEFAULT_MILESTONES);

  const { data: milestonesData, loading: loadingMilestones } = useQuery<
    ListChannelMonetizationMilestones,
    ListChannelMonetizationMilestonesVariables
  >(LIST_CHANNEL_MONETIZATION_MILESTONES, {
    variables: { programId: program?.id ?? '' },
    skip: !isEdit || !program?.id || !isVisible,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!isVisible) return;

    if (isEdit && program) {
      form.setFieldsValue({
        monthLabel: program.monthLabel,
        prizeAmount: program.prizeAmount,
        prizeCurrency: program.prizeCurrency,
        slotsPerMonth: program.slotsPerMonth,
      });
    } else {
      form.setFieldsValue({
        monthLabel: currentMonthLabel(),
        ...DEFAULT_PROGRAM_FORM_VALUES,
      });
      setMilestones(DEFAULT_MILESTONES);
    }
  }, [isVisible, isEdit, program, form]);

  useEffect(() => {
    const remoteMilestones = milestonesData?.listChannelMonetizationMilestones;
    if (isEdit && remoteMilestones && remoteMilestones.length > 0) {
      setMilestones(remoteMilestones.map(toMilestoneRow));
    }
  }, [isEdit, milestonesData]);

  const updateMilestone = (
    type: ChannelMonetizationMilestoneType,
    patch: Partial<MilestoneRow>
  ) => {
    setMilestones((prev) => patchMilestoneRows(prev, type, patch));
  };

  return {
    form,
    milestones,
    loadingMilestones,
    updateMilestone,
  };
};
